<script setup lang="ts">
import { computed } from 'vue';
import type { Category } from '@ilteducation/content-ref-data-types';
import { getThemeName } from './gui';

const props = defineProps<{
  subject: Category;
  size: 'small' | 'medium-small' | 'medium' | 'large';
}>();

const subjectBackground = computed(
  () => `url(${new URL(`/src/assets/images/patterns/circle/${props.subject.color}.png`, import.meta.url).href})`,
);
</script>

<template>
  <v-sheet
    :theme="getThemeName(subject.slug)"
    :style="`background-image: ${subjectBackground}`"
    :class="`category-bubble category-bubble__${size} d-flex justify-center align-center bg-subject-original`"
  >
    <span aria-hidden="true">{{ subject.categoryCode }}</span>
  </v-sheet>
</template>

<style scoped lang="scss">
@use 'vuetify/settings';

.category-bubble {
  background-position: center center;
  background-size: cover;
  border-radius: 100%;
  text-align: center;
  text-transform: capitalize;
  font-family: 'Chalkduster', sans-serif;
  aspect-ratio: 1;

  &__small {
    display: inline-block;
    line-height: 20px;
    height: 1.75rem;
    width: 1.75rem;
    padding: 5px;
    text-align: center;
    font-size: 0.75rem;

    @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
      span {
        font-size: 0.875rem;
        line-height: 1.375rem;
      }
      height: 2rem;
      width: 2rem;
      padding: 5px;
    }
  }

  &__medium {
    display: inline-block;
    height: 6.25rem;
    width: 6.25rem;
    padding: 1.25rem 0.8125rem;
    font-size: 2rem;
    margin: 0.625rem 0 0;

    @media #{map-get(settings.$display-breakpoints, 'sm')} {
      margin: 0;
      height: 80px;
      width: 80px;
      padding: 1rem 0.6125rem;
      font-size: 1.6rem;
    }

    @media #{map-get(settings.$display-breakpoints, 'xs')} {
      margin-top: 10px;
    }
  }

  &__medium-small {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75px;
    width: 75px;
    font-size: 1.5rem;
    margin: 0;
  }

  &__large {
    height: 130px;
    width: 130px;
    margin: 0 1rem 1rem 1rem;
    font-size: 2rem;

    @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
      width: 170px;
      height: 170px;
    }

    @media #{map-get(settings.$display-breakpoints, 'lg-and-up')} {
      width: 190px;
      height: 190px;
      font-size: 2rem;
    }
  }
}
</style>
